import { useSelector } from "react-redux";
import SidePanel from "./SidePanel";
import { RootState, WeekStartDay } from "../../../store";
import { useMemo } from "react";
import {
  additionalCalendarSelectionUpdated,
  hiddenCalendarIdsUpdated,
  leaderSelectionUpdated, selectDisplayedAdditionalCalendars, selectMiddleOfInterval, selectTimezone, updateDateRange
} from "../../../store/scheduleUI";
import { useHandler } from "../../../store/hooks";
import { selectOrderedSchedulingLeaders } from "../../../store/leaders/selectors";
import { selectLeaderIdCalendarMap, selectLeaderIdPrimaryCalendarMap } from "../../../store/schedule/selectors";
import { scheduling } from "../../../store/cabinetApi/scheduling";
import { View as RBCView } from "react-big-calendar";

const SidePanelContainer = ({ open, onCancel }: {
  open: boolean;
  onCancel: () => void;
}) => {
  const schedulingLeaders = useSelector(selectOrderedSchedulingLeaders);
  const displayedAdditionalCalendars = useSelector(selectDisplayedAdditionalCalendars);
  const middleOfInterval = useSelector(selectMiddleOfInterval);
  const timezone = useSelector(selectTimezone);
  const hiddenCalendarIds = useSelector((state: RootState) => state.scheduleUI.hiddenCalendarIds);
  const leaderIdCalendarMap = useSelector(selectLeaderIdCalendarMap);
  const leaderIdPrimaryCalendarMap = useSelector(selectLeaderIdPrimaryCalendarMap);
  const calendarsLoaded = useSelector((state: RootState) => state.schedule.calendarsLoaded);
  const meetingsLoaded = useSelector((state: RootState) => state.schedule.meetingsLoaded);
  const selectedLeaders = useSelector((state: RootState) => state.scheduleUI.selectedLeaderIds);
  const selectedAdditionalCalendarAccessIds = useSelector(
    (state: RootState) => state.scheduleUI.selectedAdditionalCalendarAccessIds
  );
  const { pinnedCalendars } = scheduling.useAppSchedulingPinnedCalendarsListQuery(undefined, {
    selectFromResult: ({ data }) => ({
      pinnedCalendars: data || [],
    }),
  });
  const [pinCalendar] = scheduling.useAppSchedulingPinnedCalendarsCreateMutation();
  const [unpinCalendar] = scheduling.useAppSchedulingPinnedCalendarsDestroyMutation();
  
  const { weekStartDay, weekView } = scheduling.useAppSchedulingPreferencesListQuery(undefined, {
    selectFromResult: ({ data }) => ({
      weekStartDay: data?.user_prefs?.week_start_day,
      weekView: data?.user_prefs?.week_view,
    }),
  });
  
  // Convert week_view string to RBCView enum
  const calendarView: RBCView = weekView === "WEEK" ? "week" as RBCView
    : weekView === "DAY" ? "day" as RBCView
      : "work_week" as RBCView; // Default to work_week

  const allLoaded = calendarsLoaded && meetingsLoaded;

  const handle = useHandler();

  const additionalCalendars = useMemo(() => {
    const pinnedCalendarIdByAccessId = new Map(
      pinnedCalendars.map(pinnedCalendar => [pinnedCalendar.calendar_access, pinnedCalendar.id])
    );
    const selectedCalendarAccessIds = new Set(selectedAdditionalCalendarAccessIds);
    return displayedAdditionalCalendars.map(cal => ({
      ...cal,
      title: cal.additionalCalendarEmail?.name || cal.summary || cal.owner_email || '',
      subtitle: cal.owner_email || '',
      canEdit: !!cal.additionalCalendarEmail || cal.canEdit,
      pinnedCalendarId: pinnedCalendarIdByAccessId.get(cal.calendar_access_id),
      selected: selectedCalendarAccessIds.has(cal.calendar_access_id),
    }));
  }, [displayedAdditionalCalendars, pinnedCalendars, selectedAdditionalCalendarAccessIds]);

  const handleTogglePinnedCalendar = async (calendarAccessId: number, pinnedCalendarId?: number) => {
    if (pinnedCalendarId != null) {
      await unpinCalendar(pinnedCalendarId).unwrap();
    } else {
      await pinCalendar({ calendar_access: calendarAccessId }).unwrap();
    }
  };

  return (
    <SidePanel
      open={open}
      onCancel={onCancel}
      leaders={schedulingLeaders}
      additionalCalendars={additionalCalendars}
      selectedLeaders={selectedLeaders}
      onLeaderSelect={handle(leaderSelectionUpdated)}
      middleOfInterval={middleOfInterval}
      onUpdateDateRange={handle(updateDateRange)}
      hiddenCalendarIds={hiddenCalendarIds}
      onToggleCalendarVisibility={handle(hiddenCalendarIdsUpdated)}
      leaderIdCalendarMap={leaderIdCalendarMap}
      leaderIdPrimaryCalendarMap={leaderIdPrimaryCalendarMap}
      calendarView={calendarView}
      allLoaded={allLoaded}
      onCheckAdditionalCalendar={handle(additionalCalendarSelectionUpdated)}
      onTogglePinnedCalendar={handleTogglePinnedCalendar}
      weekStartDay={weekStartDay ? WeekStartDay[weekStartDay] : undefined}
      timezoneName={timezone.name}
    />
  );
};

export default SidePanelContainer;
